import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import {
  Window,
  WindowActionsBar,
  Dialog
} from '@progress/kendo-react-dialogs';
import { Input, TextArea } from '@progress/kendo-react-inputs';

import { appStore } from '../../stores/app-store';
import { ZoneLibraryWorldPreview } from './world-map';
import { useToast } from '../../contexts/toast-context';
import { ZoneLibraryIllustrationPreview } from './illustration';
import { ZoneCopyToGameWindow } from '../zone-copy-to-game-window';
import { useZoneLibraryById } from '../../hooks/services/use-zones-library';
import SubmitConfirmation from '../../components/submit-to-global-library';
import { SubmitZoneAsync, AddZoneLibraryAsync } from '../../services/zone';

export const ZonePreviewWindow: React.FC<{
  zoneId: string;
  onClose: Function;
  isAdmin?: boolean;
  isGlobal?: boolean;
  onDelete?: (zoneId: string) => void;
}> = ({ zoneId, onClose, isGlobal, isAdmin, onDelete }) => {
  const { data, isLoading } = useZoneLibraryById(zoneId, isGlobal);
  const { onToastOpen } = useToast();
  const [showSubmitDialog, setShowSubmitDialog] = React.useState(false);
  const [showCopyWindow, setShowCopyWindow] = React.useState(false);
  const [copyZoneName, setCopyZoneName] = React.useState('');
  const [copyZoneDescription, setCopyZoneDescription] = React.useState('');
  const [isCopying, setIsCopying] = React.useState(false);
  const [showCopyToGameModal, setShowCopyToGameModal] = React.useState(false);

  const handleOpenCopyWindow = () => {
    if (data) {
      setCopyZoneName(data.name);
      setCopyZoneDescription(data.description);
      setShowCopyWindow(true);
    }
  };

  const handleCopyZoneSubmit = async () => {
    if (!data || isCopying) return;
    try {
      setIsCopying(true);
      appStore.showLoading();

      const payload = {
        name: copyZoneName,
        description: copyZoneDescription,
        previewImageUrl: (data as any).previewImageUrl ?? '',
        content: data.content
      };

      await AddZoneLibraryAsync(payload);
      onToastOpen({
        title: 'Success',
        body: 'Zone map copied to library.',
        style: 'success'
      });
      setShowCopyWindow(false);
      onClose();
    } catch (error) {
      onToastOpen({
        title: 'Error',
        body: 'Failed to copy zone map to library.',
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      setIsCopying(false);
    }
  };

  const mapType = React.useMemo(() => {
    if (!data?.content) return 'openStreetMap';

    return (data.content.maps ?? []).at(0)?.type ?? 'openStreetMap';
  }, [data]);

  const onSubmitGlobalLibraryHandler = async () => {
    try {
      appStore.showLoading();
      await SubmitZoneAsync(parseInt(zoneId));
      onToastOpen({
        title: 'Success',
        body: 'Zone map submitted to global library and waiting approval.',
        style: 'success'
      });
      onClose();
    } catch (error) {
      onToastOpen({
        title: 'Error',
        body: 'Failed to submit zone map to global library.',
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      setShowSubmitDialog(false);
    }
  };

  return (
    <>
      <Window
        title={'Preview'}
        initialWidth={750}
        initialHeight={650}
        onClose={() => onClose()}
        className={'zone-library'}>
        {isLoading || !data?.content ? (
          <div
            className={
              'd-flex flex-column w-100 h-100 align-items-center justify-content-center'
            }>
            <Loader type={'infinite-spinner'} size={'large'} />
            <span>Loading zone map content</span>
          </div>
        ) : mapType !== 'illustrated' ? (
          <ZoneLibraryWorldPreview content={data.content} />
        ) : (
          <ZoneLibraryIllustrationPreview content={data.content} />
        )}

        <WindowActionsBar>
          <Button themeColor={'secondary'} onClick={() => onClose()}>
            Close
          </Button>
          {!isGlobal && (
            <>
              <Button
                themeColor={'success'}
                onClick={() => setShowCopyToGameModal(true)}>
                Copy to game
              </Button>
              <Button
                themeColor={'primary'}
                disabled={
                  isLoading ||
                  data?.status === 'Pending' ||
                  data?.status === 'Approved'
                }
                onClick={() => setShowSubmitDialog(true)}>
                Submit to global library
              </Button>
            </>
          )}
          {isGlobal && !isAdmin && (
            <Button
              themeColor={'primary'}
              disabled={isLoading}
              onClick={handleOpenCopyWindow}>
              Copy to My Zone Map Library
            </Button>
          )}
          {isGlobal && isAdmin && (
            <Button themeColor={'error'} onClick={() => onDelete?.(zoneId)}>
              Delete from Global Library
            </Button>
          )}
        </WindowActionsBar>
      </Window>

      {showSubmitDialog && (
        <Dialog
          width={'50vw'}
          title={
            <span className="fw-bold text-primary">
              Submit to global library
            </span>
          }
          onClose={() => setShowSubmitDialog(false)}>
          <SubmitConfirmation
            toggleDialog={() => setShowSubmitDialog(false)}
            onSubmit={onSubmitGlobalLibraryHandler}
            isPendingApproval={data?.status === 'Pending'}
          />
        </Dialog>
      )}

      {showCopyToGameModal && (
        <ZoneCopyToGameWindow
          zoneId={zoneId}
          onClose={() => {
            setShowCopyToGameModal(false);
          }}
        />
      )}

      {showCopyWindow && (
        <Window
          title={'Copy Zone Map to My Zone Map Library'}
          onClose={() => {
            if (isCopying) return;
            setShowCopyWindow(false);
          }}
          initialWidth={400}
          initialHeight={350}
          className={'zone-library'}>
          <div className="mb-3">
            <label className="k-label">Name</label>
            <Input
              value={copyZoneName}
              onChange={(e) => setCopyZoneName(e.value)}
              disabled={isCopying}
              style={{ width: '100%' }}
            />
          </div>
          <div className="mb-3">
            <label className="k-label">Description</label>
            <TextArea
              value={copyZoneDescription}
              onChange={(e) => setCopyZoneDescription(e.value)}
              disabled={isCopying}
              style={{ width: '100%' }}
            />
          </div>
          <WindowActionsBar>
            <Button
              themeColor={'secondary'}
              disabled={isCopying}
              onClick={() => setShowCopyWindow(false)}>
              Cancel
            </Button>
            <Button
              themeColor={'primary'}
              disabled={isCopying || !copyZoneName || !copyZoneDescription}
              onClick={handleCopyZoneSubmit}>
              {isCopying ? 'Copying...' : 'Copy'}
            </Button>
          </WindowActionsBar>
        </Window>
      )}
    </>
  );
};
